"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AgentService = void 0;
const errors_1 = require("../errors");
const graphql_request_1 = require("graphql-request");
class AgentService {
    constructor(agentRegistry, signer, ipfsSDK, subgraphUrl) {
        this.agentRegistry = agentRegistry;
        this.signer = signer;
        this.ipfsSDK = ipfsSDK;
        if (subgraphUrl) {
            this.subgraphClient = new graphql_request_1.GraphQLClient(subgraphUrl);
        }
    }
    /**
     * Gets the address of the agent.
     * @returns {Promise<string>} A promise that resolves to the agent.
     */
    async getAgentAddress() {
        return this.signer.getAddress();
    }
    /**
     * Registers a new agent without service.
     * @param {string} address - The address of the agent.
     * @param {AgentMetadata} metadata - The metadata of the agent.
     * @returns {Promise<boolean>} A promise that resolves to the agent registration status.
     */
    async registerAgent(address, metadata) {
        try {
            if (!this.ipfsSDK) {
                throw new Error("IPFS SDK is not initialized");
            }
            console.log(`registering agent ${address} with metadata: ${metadata}`);
            const uploadResponse = await this.ipfsSDK.upload.json(metadata);
            const agentURI = `ipfs://${uploadResponse.IpfsHash}`;
            const tx = await this.agentRegistry.registerAgent(address, metadata.name, agentURI);
            console.log(`transaction to register agent was sent. tx: ${tx}`);
            await tx.wait();
            return true;
        }
        catch (error) {
            console.error({ error });
            if (error.reason === "Agent already registered") {
                throw new errors_1.AgentAlreadyRegisteredError(error.reason);
            }
            else {
                throw error;
            }
        }
    }
    /**
     * Registers a new agent with service.
     * @param {string} address - The address of the agent..
     * @param {AgentMetadata} metadata - The metadata of the agent.
     * @param {string} serviceName - The name of the service.
     * @param {number} servicePrice - The price of the service.
     * @param {string} tokenAddress - The token address for payment.
     * @returns {Promise<boolean>} A promise that resolves to the agent registration status.
     */
    async registerAgentWithService(address, metadata, serviceName, servicePrice, tokenAddress = "0x0000000000000000000000000000000000000000" // Default to zero address for ETH
    ) {
        try {
            if (!this.ipfsSDK) {
                throw new Error("IPFS SDK is not initialized");
            }
            console.log(`registering agent ${address} with metadata: ${metadata}`);
            const uploadResponse = await this.ipfsSDK.upload.json(metadata);
            const agentURI = `ipfs://${uploadResponse.IpfsHash}`;
            const tx = await this.agentRegistry.registerAgentWithService(address, metadata.name, agentURI, serviceName, servicePrice, tokenAddress);
            console.log(`transaction to register agent was sent. tx: ${tx}`);
            await tx.wait();
            return true;
        }
        catch (error) {
            console.error({ error });
            if (error.reason === "Service not registered") {
                throw new errors_1.ServiceNotRegisteredError(error.reason);
            }
            else if (error.reason === "Agent already registered") {
                throw new errors_1.AgentAlreadyRegisteredError(error.reason);
            }
            else {
                throw error;
            }
        }
    }
    /**
     * Add a proposal for an agent.
     * @param {string} agentAddress The address of the agent.
     * @param {string} serviceName The name of the service.
     * @param {number} servicePrice The price of the service.
     * @param {string} tokenAddress The token address for payment.
     * @returns {Promise<boolean>} A promise that resolves to a boolean indicating if the proposal was added.
     */
    async addProposal(agentAddress, serviceName, servicePrice, tokenAddress) {
        try {
            const tx = await this.agentRegistry.addProposal(agentAddress, serviceName, servicePrice, tokenAddress);
            const receipt = await tx.wait();
            return receipt ? true : false;
        }
        catch (error) {
            console.error(error);
            return false;
        }
    }
    /**
     * Remove the proposal of an agent.
     * @param {string} agentAddress The address of the agent.
     * @param {string} proposalId The ID of the proposal.
     * @returns {Promise<boolean>} A promise that resolves to a boolean indicating if the proposal was removed.
     */
    async removeProposal(agentAddress, proposalId) {
        try {
            const tx = await this.agentRegistry.removeProposal(agentAddress, proposalId);
            const receipt = await tx.wait();
            return receipt ? true : false;
        }
        catch (error) {
            console.error(error);
            return false;
        }
    }
    /**
     * Gets data for a specific agent.
     * @param {string} agentAddress - The address of the agent.
     * @returns {Promise<AgentData>} A promise that resolves to the agent data.
     */
    async getAgent(agentAddress) {
        const { name, agentUri, owner, agent, reputation, totalRatings } = await this.agentRegistry.getAgentData(agentAddress);
        return {
            name,
            agentUri,
            owner,
            agent,
            reputation,
            totalRatings,
        };
    }
    /**
     * Gets a proposal by ID.
     * @param {string} proposalId - The ID of the proposal.
     * @returns {Promise<Proposal>} A promise that resolves to the proposal.
     */
    async getProposal(proposalId) {
        const { proposalId: id, issuer, price, serviceName, tokenAddress, isActive, } = await this.agentRegistry.getProposal(proposalId);
        return {
            id,
            issuer,
            price,
            serviceName,
            tokenAddress,
            isActive,
        };
    }
    /**
     * Updates the metadata of an existing agent.
     * @param {string} agentAddress - The address of the agent to update.
     * @param {AgentMetadata} metadata - The new metadata for the agent.
     * @returns {Promise<boolean>} A promise that resolves to true if the update was successful.
     */
    async updateAgentMetadata(agentAddress, metadata) {
        try {
            if (!this.ipfsSDK) {
                throw new Error("IPFS SDK is not initialized");
            }
            console.log(`updating agent ${agentAddress} with metadata: ${JSON.stringify(metadata)}`);
            // Upload new metadata to IPFS
            const uploadResponse = await this.ipfsSDK.upload.json(metadata);
            const agentURI = `ipfs://${uploadResponse.IpfsHash}`;
            // Update agent data on the blockchain
            const tx = await this.agentRegistry.setAgentData(agentAddress, metadata.name, agentURI);
            console.log(`transaction to update agent metadata was sent. tx: ${tx.hash}`);
            await tx.wait();
            return true;
        }
        catch (error) {
            console.error("Error updating agent metadata:", error);
            if (error.reason === "Agent not registered") {
                throw new Error("Agent not registered");
            }
            else if (error.reason === "Not the owner of the agent") {
                throw new Error("Not the owner of the agent");
            }
            else {
                throw error;
            }
        }
    }
    /**
     * Gets all agents owned by a specific address.
     * @param {string} ownerAddress - The address of the owner.
     * @returns {Promise<AgentData[]>} A promise that resolves to an array of agent data.
     */
    async getAgentsByOwner(ownerAddress) {
        if (!this.subgraphClient) {
            throw new Error("Subgraph client is not initialized. Please provide a subgraphUrl in the config.");
        }
        const query = (0, graphql_request_1.gql) `
      query GetAgentsByOwner($owner: String!) {
        agents(where: { owner: $owner }) {
          id
          name
          agentUri
          owner
          reputation
          metadata {
            name
            description
            imageUri
          }
          proposals {
            id
            service
            price
          }
        }
      }
    `;
        try {
            const result = await this.subgraphClient.request(query, {
                owner: ownerAddress.toLowerCase()
            });
            return result.agents.map(agent => ({
                name: agent.name,
                agentUri: agent.agentUri,
                owner: agent.owner,
                agent: agent.id,
                reputation: BigInt(agent.reputation),
                totalRatings: BigInt(0) // Note: totalRatings would need to be added to subgraph schema
            }));
        }
        catch (error) {
            console.error("Error fetching agents by owner:", error);
            throw new Error(`Failed to fetch agents for owner ${ownerAddress}: ${error}`);
        }
    }
    /**
     * The reputation of an agent.
     * @param {string} agentAddress The address of the agent
     * @returns {Promise<bigint>} A promise that resolves to the reputation of the agent.
     */
    async getReputation(agentAddress) {
        return this.agentRegistry.getReputation(agentAddress);
    }
}
exports.AgentService = AgentService;
